import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
	ChevronRight,
	Check,
	CheckCircle2,
	AlertCircle,
	Loader2,
	User,
	Gift,
	X,
	Calendar,
	Clock,
	Video,
	MapPin,
	Star,
	Tag,
} from 'lucide-react';
import {
	useCreateSession,
	useGetProfessional,
	useGetProfessionalPublic,
	useGetService,
	useGetSessions,
	usePaySession,
} from '@/services';
import { useJoinClub } from '@/services/mutations/club';
import { useGetClubsAvailable, useGetMyClub } from '@/services/queries/club';
import { ClubIcon, CopyWrapper } from '@/components/ui';
import { PaymentModalContainerSkeleton } from './PaymentModalContainerSkeleton';
import { cn } from '@/lib/utils';
import {
	encodeUrl,
	formatProfessionalFullName,
	getSessionDisplayTitle,
	resolveSchedulingHistoryForClubUpsell,
	markSchedulingPayHandoffToAuth,
	saveSchedulingPayDraft,
	SCHEDULING_PREVIEW_SESSION_ID,
} from '@/utils';
import { getUserData } from '@/utils/cookies';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';
import {
	SCHEDULING_FIRST_SESSION_PROMO_CODE,
	SCHEDULING_FIRST_SESSION_PROMO_PRICE_BRL,
} from '@/constants/schedulingFirstSessionPromo';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useModal } from '@/hooks/useModal';
import { useUserSessionAccessWindow } from '@/hooks/useUserSessionAccessWindow';
import BenefitTermsOfUsePage from '@/pages/benefit-terms-of-use';
import { buildCheckoutRedirectUrl } from '@/utils/checkoutRedirectOrigin';
import {
	getBenefitHasAvailableUses,
	getBenefitRemainingBalance,
} from '@/utils/clubBenefitUsage';
import { openUserSessionCall } from '@/utils/userSessionCall';
import { PaymentEmbeddedScrollHint } from './PaymentEmbeddedScrollHint';
import { StepButtonContainer } from '@/components';

/** Serviço do benefício deve ser tipo PROFESSION para poder usar na sessão (backend usa ServiceType.PROFESSION). */
const SERVICE_TYPE_PROFESSION = 'PROFESSION' as Services.ServiceType;

function normalizeServiceSpecialtyText(value: string): string {
	return value
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
}

function isPsychologistServiceText(
	...parts: (string | null | undefined)[]
): boolean {
	const haystack = parts
		.filter((v): v is string => Boolean(v?.trim()))
		.map(normalizeServiceSpecialtyText)
		.join(' ');
	return haystack.includes('psicolog');
}

const UPSELL_BENEFIT_INTENT_KEY = 'plumaa_upsell_benefit_intent';

function saveUpsellBenefitIntent(serviceId: string, discountPct: number) {
	try {
		sessionStorage.setItem(
			UPSELL_BENEFIT_INTENT_KEY,
			JSON.stringify({ serviceId, discountPct }),
		);
	} catch {}
}

function readAndClearUpsellBenefitIntent(): {
	serviceId: string;
	discountPct: number;
} | null {
	try {
		const raw = sessionStorage.getItem(UPSELL_BENEFIT_INTENT_KEY);
		if (!raw) return null;
		sessionStorage.removeItem(UPSELL_BENEFIT_INTENT_KEY);
		return JSON.parse(raw) as { serviceId: string; discountPct: number };
	} catch {
		return null;
	}
}

function findProfessionBenefitsForService(
	club: Clubs.Club,
	serviceId: string,
): Clubs.Benefit[] {
	let benefits: Clubs.Benefit[] = [];
	for (const b of club.benefits ?? []) {
		if (b.service_id !== serviceId) continue;
		if (b.service?.type !== SERVICE_TYPE_PROFESSION) continue;
		if (
			!benefits.length ||
			(b.service_discount_percentage ?? 0) >
				(benefits[0].service_discount_percentage ?? 0 ?? 0)
		) {
			benefits = [...benefits, b];
		}
	}
	return benefits;
}

/** Forma do user_club_subscription_benefit vinda do GET /clubs/me (com club_benefit incluído). */
interface UserClubSubscriptionBenefitForEligibility {
	id: string;
	used_count: number;
	club_benefit_id: string;
	club_benefit: {
		service_id: string;
		default_amount: number;
		service_substitute_title?: string | null;
		service_discount_percentage: number;
		display_order?: number;
		service?: { id: string; type?: string; service_name?: string };
	};
}

function benefitDisplayTitle(
	benefit: UserClubSubscriptionBenefitForEligibility,
	fallbackServiceName: string,
) {
	if (benefit.club_benefit.service?.service_name)
		return benefit.club_benefit.service.service_name;
	if (benefit.club_benefit.service_substitute_title)
		return benefit.club_benefit.service_substitute_title.trim();
	return fallbackServiceName;
}

function benefitDiscountPct(
	benefit: UserClubSubscriptionBenefitForEligibility,
) {
	return Math.min(
		100,
		Math.max(0, benefit.club_benefit.service_discount_percentage),
	);
}

/** Texto curto do cupom selecionado (card principal). */
function BenefitDiscountHighlightLabel({
	benefit,
}: {
	benefit: UserClubSubscriptionBenefitForEligibility;
}) {
	const pct = benefitDiscountPct(benefit);
	return (
		<>
			Desconto de <span className="text-brand-green">{pct}%</span>
		</>
	);
}

/** Linha principal do modal Benefícios: desconto em (título do serviço). */
function BenefitPickerDiscountTitleLine({
	benefit,
	headingServiceName,
}: {
	benefit: UserClubSubscriptionBenefitForEligibility;
	headingServiceName: string;
}) {
	const title = benefitDisplayTitle(benefit, headingServiceName);
	const pct = benefitDiscountPct(benefit);

	return (
		<>
			Desconto de <span className="text-brand-green">{pct}%</span> na
			sessão de {title}
		</>
	);
}

function BenefitPickerOptionRow({
	benefit,
	headingServiceName,
	selected,
	onClick,
	hideUsageRemaining,
	recommended,
	dataTestId,
}: {
	benefit: UserClubSubscriptionBenefitForEligibility;
	headingServiceName: string;
	selected?: boolean;
	onClick: () => void;
	hideUsageRemaining?: boolean;
	recommended?: boolean;
	dataTestId?: string;
}) {
	const remaining = getBenefitRemainingBalance(
		benefit.club_benefit.default_amount,
		benefit.used_count,
	);

	return (
		<button
			type="button"
			data-testid={dataTestId}
			onClick={onClick}
			className={cn(
				'group box-border flex w-full items-center gap-3 rounded-2xl border-2 px-3 py-4 text-left',
				'transition-all duration-150',
				selected
					? [
							'border-brand-orange bg-white shadow-none',
							'translate-x-1 translate-y-1',
					  ]
					: [
							'border-stone-300 bg-stone-50',
							'shadow-[4px_4px_0px_0px_rgba(168,162,158,0.55)]',
							'hover:translate-x-[2px] hover:translate-y-[2px] hover:border-brand-orange hover:shadow-none',
					  ],
			)}
		>
			{selected ? (
				<div className="shrink-0 rounded-xl border-2 border-brand-orange/40 bg-brand-orange/10 p-2">
					<Gift className="size-5 text-brand-orange" />
				</div>
			) : (
				<div className="relative size-10 shrink-0 overflow-hidden rounded-xl">
					{/* Spinning gradient — larger than container so corners stay covered as it rotates */}
					<div
						className="absolute -left-1/2 -top-1/2 h-[200%] w-[200%] animate-gift-border-spin"
						style={{
							background:
								'conic-gradient(from 0deg, oklch(84.1% 0.238 128.85) 0%, oklch(84.1% 0.238 128.85) 40%, #4ade80 50%, #4ade80 90%, oklch(84.1% 0.238 128.85) 100%)',
						}}
					/>
					{/* White inner area — 2px inset creates the gradient border */}
					<div className="absolute inset-[2px] z-10 flex items-center justify-center overflow-hidden rounded-[9px] bg-white">
						<AnimatedGiftIcon />
					</div>
				</div>
			)}
			<div className="min-w-0 flex-1 space-y-0.5">
				<p className="line-clamp-2 text-sm font-extrabold leading-snug text-stone-900">
					<BenefitPickerDiscountTitleLine
						benefit={benefit}
						headingServiceName={headingServiceName}
					/>
				</p>
				{!hideUsageRemaining && (
					<p className="truncate text-xs font-medium text-stone-600">
						{remaining} uso(s) restante(s)
					</p>
				)}
				{recommended && (
					<span className="inline-flex items-center rounded-full bg-brand-green/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-brand-green">
						ideal para você
					</span>
				)}
			</div>
			<div
				className={cn(
					'shrink-0 rounded-lg border-2 p-1.5 transition-all duration-150',
					selected
						? 'border-brand-orange bg-brand-orange'
						: [
								'border-stone-300 bg-white',
								'shadow-[3px_3px_0px_0px_rgba(168,162,158,0.45)]',
								'group-hover:border-brand-orange group-hover:bg-brand-orange group-hover:shadow-none',
						  ],
				)}
			>
				{selected ? (
					<Check
						className="size-4 text-white"
						strokeWidth={2.5}
						aria-hidden
					/>
				) : (
					<ChevronRight
						className="size-4 text-stone-400 transition-colors duration-150 group-hover:text-white"
						aria-hidden
					/>
				)}
			</div>
		</button>
	);
}

function ClubBenefitPickerPanel({
	benefits,
	headingServiceName,
	selectedBenefitId,
	onPick,
	onClose,
}: {
	benefits: UserClubSubscriptionBenefitForEligibility[];
	headingServiceName: string;
	selectedBenefitId: string | null;
	onPick: (benefit: UserClubSubscriptionBenefitForEligibility | null) => void;
	onClose: () => void;
}) {
	const [showTerms, setShowTerms] = useState(false);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="club-benefit-picker-title"
			data-testid={E2E_SCHEDULING.CLUB_BENEFIT_PICKER_PANEL}
			className="flex w-full flex-col gap-4 overflow-hidden rounded-2xl bg-white p-5"
		>
			{showTerms ? (
				<div className="max-h-[min(50vh,22rem)] space-y-4 overflow-y-auto">
					<h2
						id="club-benefit-picker-title"
						className="text-lg font-bold text-stone-900"
					>
						Termos de Uso do Benefício
					</h2>
					<BenefitTermsOfUsePage />
				</div>
			) : (
				<div className="space-y-4">
					<div className="space-y-1">
						<h2
							id="club-benefit-picker-title"
							className="text-lg font-bold leading-snug text-stone-900"
						>
							Benefícios disponíveis
						</h2>
						<p className="text-sm leading-relaxed text-stone-500">
							Escolha um benefício para aplicar na lista de custo.
						</p>
					</div>
					<div className="flex flex-col gap-2.5 md:gap-3">
						<button
							type="button"
							onClick={() => onPick(null)}
							className={cn(
								'group box-border flex w-full items-center gap-3 rounded-2xl border-2 px-3 py-4 text-left transition-all duration-150',
								selectedBenefitId === null
									? 'translate-x-1 translate-y-1 border-brand-orange bg-white shadow-none'
									: 'border-stone-300 bg-stone-50 shadow-[4px_4px_0px_0px_rgba(168,162,158,0.55)] hover:translate-x-[2px] hover:translate-y-[2px] hover:border-brand-orange hover:shadow-none',
							)}
						>
							<div
								className={cn(
									'shrink-0 rounded-xl border-2 p-2',
									selectedBenefitId === null
										? 'border-brand-orange/40 bg-brand-orange/10'
										: 'border-stone-200 bg-white',
								)}
							>
								<X
									className={cn(
										'size-5',
										selectedBenefitId === null
											? 'text-brand-orange'
											: 'text-stone-400',
									)}
								/>
							</div>
							<div className="min-w-0 flex-1">
								<p className="text-sm font-extrabold text-stone-900">
									Não quero benefício
								</p>
								<p className="text-xs font-medium text-stone-600">
									Pagamento sem desconto do clube
								</p>
							</div>
						</button>
						{benefits.map((benefit) => (
							<BenefitPickerOptionRow
								key={benefit.id}
								benefit={benefit}
								headingServiceName={headingServiceName}
								selected={selectedBenefitId === benefit.id}
								dataTestId={`${E2E_SCHEDULING.BENEFIT_OPTION_PREFIX}${benefit.id}`}
								onClick={() => onPick(benefit)}
							/>
						))}
					</div>
					<button
						type="button"
						onClick={() => setShowTerms(true)}
						className="text-xs font-semibold text-stone-700 underline-offset-2 hover:underline"
					>
						Termos de Uso do Benefício
					</button>
				</div>
			)}

			<StepButtonContainer
				onHandlePrevius={
					showTerms ? () => setShowTerms(false) : onClose
				}
				showNextButton={false}
			/>
		</div>
	);
}

function AnimatedGiftIcon({ className: _ }: { className?: string }) {
	return (
		<span className="relative inline-flex size-5 shrink-0">
			{/* Base layer: always green */}
			<Gift className="size-5 text-green-500" />
			{/* Outer: rotates + pulses clip width → sweeps the second color around the icon */}
			<span className="absolute inset-0 animate-gift-sweep">
				{/* Inner: counter-rotates so the icon itself stays upright */}
				<Gift
					className="size-5 animate-gift-counter-sweep text-[oklch(78%_0.196_126.665)]"
					aria-hidden
				/>
			</span>
		</span>
	);
}

function sortClubsForUpsell(
	clubs: Clubs.Club[],
	serviceId: string,
): { club: Clubs.Club; isServiceMatch: boolean; discountPct: number }[] {
	return clubs
		.map((club) => {
			const matchingBenefit = (club.benefits ?? []).find(
				(b) =>
					b.service_id === serviceId &&
					b.service?.type === SERVICE_TYPE_PROFESSION,
			);
			return {
				club,
				isServiceMatch: !!matchingBenefit,
				discountPct: matchingBenefit?.service_discount_percentage ?? 0,
			};
		})
		.sort((a, b) => {
			if (a.isServiceMatch !== b.isServiceMatch)
				return a.isServiceMatch ? -1 : 1;
			if (a.isServiceMatch && b.isServiceMatch)
				return b.discountPct - a.discountPct;
			return Number(a.club.price) - Number(b.club.price);
		});
}

function UpsellClubCard({
	club,
	serviceId,
	isLoading,
	isDisabled,
	onJoin,
	recommended,
}: {
	club: Clubs.Club;
	serviceId: string;
	isLoading: boolean;
	isDisabled: boolean;
	onJoin: (clubId: string) => void;
	recommended?: boolean;
}) {
	const sortedBenefits = [...(club.benefits ?? [])].sort((a, b) => {
		const aMatch =
			a.service_id === serviceId &&
			a.service?.type === SERVICE_TYPE_PROFESSION;
		const bMatch =
			b.service_id === serviceId &&
			b.service?.type === SERVICE_TYPE_PROFESSION;
		if (aMatch !== bMatch) return aMatch ? -1 : 1;
		const aPct = a.service_discount_percentage ?? 0;
		const bPct = b.service_discount_percentage ?? 0;
		if (bPct !== aPct) return bPct - aPct;
		return (b.display_order ?? 0) - (a.display_order ?? 0);
	});

	const rawPrice = parseFloat(club.price || '0');
	const formatPrice = (value: number) =>
		value.toLocaleString('pt-BR', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});

	return (
		<div className="relative flex h-full flex-col justify-between gap-6 rounded-[2rem] border border-zinc-200 bg-white px-4 py-3 shadow-sm transition-all duration-300 hover:shadow-2xl hover:shadow-zinc-200/60">
			<div className="space-y-5 text-left">
				<div className="mb-2 flex items-center justify-center gap-x-2">
					<ClubIcon club={club} size="md" />
					<div className="flex flex-col items-start space-y-0.5">
						<h3 className="text-2xl font-bold leading-tight text-zinc-900">
							{club.name}
						</h3>
						<p className="text-[10px] font-black uppercase tracking-widest text-brand-orange">
							{club.currency === 'BRL' ? 'R$' : club.currency}{' '}
							{formatPrice(rawPrice)} /mês
						</p>
					</div>
				</div>

				{/* {club.slogan && (
          <p className="text-sm font-light text-zinc-400 px-1.5 text-center">{club.slogan}</p>
        )} */}

				<ul className="mt-1 space-y-1">
					{sortedBenefits.map((benefit, index) => {
						const isHighlighted = index === 0;
						const label =
							benefit.service_substitute_title ||
							benefit.service?.service_name;

						return (
							<li
								key={benefit.id}
								className={cn(
									'flex animate-card-enter items-start gap-3 rounded-xl px-1.5 py-1 text-sm',
									isHighlighted
										? 'border border-brand-orange/30 bg-brand-orange/10 py-1.5'
										: '',
								)}
								style={{ animationDelay: `${index * 100}ms` }}
							>
								{isHighlighted ? (
									<Star
										size={16}
										strokeWidth={1}
										className="mt-0.5 shrink-0 fill-brand-orange/0 text-brand-orange"
									/>
								) : (
									<Check
										size={16}
										strokeWidth={2}
										className="mt-0.5 shrink-0 text-brand-orange"
									/>
								)}
								<span
									className={cn(
										'font-medium',
										isHighlighted
											? 'font-bold text-zinc-800'
											: 'text-zinc-500',
									)}
								>
									{label}
									{isHighlighted && recommended && (
										<span className="ml-1.5 inline-flex items-center rounded-full bg-brand-orange/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-brand-orange">
											ideal para você
										</span>
									)}
								</span>
							</li>
						);
					})}
				</ul>
			</div>

			<button
				type="button"
				disabled={isDisabled}
				onClick={() => onJoin(club.id)}
				className={cn(
					'w-full rounded-[1.25rem] border-2 border-brand-orange bg-brand-orange px-4 py-4 text-[11px] font-black uppercase tracking-[0.18em] text-white shadow-sm',
					'transition-all duration-150',
					'hover:bg-brand-orange/90 active:scale-[0.97] active:shadow-none',
					isDisabled && !isLoading && 'cursor-not-allowed opacity-50',
					isLoading && 'cursor-wait',
				)}
			>
				{isLoading ? (
					<span className="inline-flex items-center justify-center gap-2">
						<Loader2 className="animate-spin" size={14} />
						Processando...
					</span>
				) : (
					'Assinar agora'
				)}
			</button>
		</div>
	);
}

function UpsellClubSubscriptionContent({
	sorted,
	serviceId,
	returnUrl,
}: {
	sorted: {
		club: Clubs.Club;
		isServiceMatch: boolean;
		discountPct: number;
	}[];
	serviceId: string;
	returnUrl: string;
}) {
	const [joiningClubId, setJoiningClubId] = useState<string | null>(null);
	const { mutateAsync: joinClubAsync } = useJoinClub((checkoutUrl) => {
		window.location.href = checkoutUrl;
	});

	const handleJoin = async (clubId: string) => {
		setJoiningClubId(clubId);
		try {
			await joinClubAsync({
				clubId,
				successUrl: returnUrl,
				cancelUrl: returnUrl,
			});
		} catch {
			setJoiningClubId(null);
		}
	};

	return (
		<div className="w-full px-0 py-1">
			<p className="mb-4 text-sm leading-relaxed text-stone-500 md:text-[15px] md:leading-relaxed">
				Garanta seu cupom de desconto e economize agora mesmo assinando
				um dos nossos clubes!
			</p>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{sorted.map(({ club }, index) => (
					<div
						key={club.id}
						className="h-full animate-card-enter"
						style={{ animationDelay: `${index * 100}ms` }}
					>
						<UpsellClubCard
							club={club}
							serviceId={serviceId}
							isLoading={joiningClubId === club.id}
							isDisabled={joiningClubId !== null}
							recommended={index === 0}
							onJoin={(clubId) => void handleJoin(clubId)}
						/>
					</div>
				))}
			</div>
		</div>
	);
}

/** Valor de referência exibido riscado quando o custo de operação é gratuito ao usuário. */
const DISPLAY_OPERATION_FEE_REFERENCE_BRL = 5;

interface PaymentContainerProps {
	session: Sessions.Session;
	className?: string;
	onPaymentClick?: () => void;
	/** Quando o pagamento é concluído sem redirecionar (ex.: fluxo no modal de agendamento). */
	onPaymentSettled?: () => void;
	navigate?: NavigateFunction;
	/** Dentro do GlobalModal: sem barra fixa no rodapé da viewport. */
	embeddedInModal?: boolean;
	/** Retorno do checkout de clube durante agendamento (permanece no passo Data/Hora). */
	schedulingCheckoutReturn?: { professionalId: string; sessionId: string };
	/** Quando definido, a sessão só é criada ao clicar em Pagar. */
	sessionCreatePayload?: Sessions.Create | null;
	onSessionCreated?: (session: Sessions.Session) => void;
}

const PaymentContainer = ({
	session,
	className,
	onPaymentClick,
	onPaymentSettled,
	navigate,
	embeddedInModal = false,
	schedulingCheckoutReturn,
	sessionCreatePayload = null,
	onSessionCreated,
}: PaymentContainerProps) => {
	const routerNavigate = useNavigate();
	const appNavigate = navigate ?? routerNavigate;
	const {
		openModal,
		closeModal,
		closeAllModals,
		setConfirmText,
		setOnConfirm,
		setCancelText,
	} = useModal();
	const professionalId =
		session.professional_id ?? session.professional?.id ?? '';
	const isDeferredSessionCreation =
		Boolean(sessionCreatePayload) && !session.id;
	const isCurrentSessionPayable = session.status === 'CREATED';
	const isLoggedIn = Boolean(getUserData());
	const usePublicProfessionalApi = !isLoggedIn && !!professionalId;

	const {
		data: professionalPrivate,
		isLoading: isLoadingProfessionalPrivate,
	} = useGetProfessional(professionalId, {
		enabled: !!professionalId && isLoggedIn,
	});
	const { data: professionalPublic, isLoading: isLoadingProfessionalPublic } =
		useGetProfessionalPublic(professionalId, {
			enabled: usePublicProfessionalApi,
		});
	const professional = isLoggedIn ? professionalPrivate : professionalPublic;
	const isLoadingProfessional = isLoggedIn
		? isLoadingProfessionalPrivate
		: isLoadingProfessionalPublic;

	const { data: service, isLoading: isLoadingService } = useGetService(
		session.service_id,
		{ enabled: isLoggedIn && !!session.service_id },
	);
	const canLoadUserCheckoutData = isCurrentSessionPayable && isLoggedIn;
	const { mutateAsync: createSession, isPending: isCreatingSession } =
		useCreateSession();

	const { data: myClub, isFetching: isFetchingMyClub } = useGetMyClub({
		enabled: canLoadUserCheckoutData,
		staleTime: 60_000,
	});
	const hasActiveClubSubscription = myClub?.subscription?.status === 'ACTIVE';

	const { data: mySessionsPage, isFetching: isFetchingMySessions } =
		useGetSessions(
			{ isMe: true, limit: 1, page: 1 },
			{
				enabled: canLoadUserCheckoutData,
				staleTime: 60_000,
			},
		);

	const historySessionId = session.id || SCHEDULING_PREVIEW_SESSION_ID;

	const hasSchedulingHistoryForClubUpsell = useMemo(
		() =>
			resolveSchedulingHistoryForClubUpsell(
				mySessionsPage,
				mySessionsPage !== undefined,
				historySessionId,
			),
		[historySessionId, mySessionsPage],
	);

	const { data: clubsAvailable, isFetching: isFetchingClubsAvailable } =
		useGetClubsAvailable({
			enabled:
				canLoadUserCheckoutData &&
				!!session.service_id &&
				!hasActiveClubSubscription &&
				hasSchedulingHistoryForClubUpsell === true,
			staleTime: 60_000,
		});
	const { isPending: isJoiningClub } = useJoinClub((checkoutUrl) => {
		window.location.href = checkoutUrl;
	});
	const isLoading = isLoadingProfessional || (isLoggedIn && isLoadingService);
	const { mutateAsync: paySession, isPending: isPaying } = usePaySession();
	const isCheckoutBusy = isPaying || isCreatingSession;

	const eligibleBenefits =
		useMemo((): UserClubSubscriptionBenefitForEligibility[] => {
			const sub = myClub?.subscription;
			if (
				!sub?.userClubSubscriptionBenefits?.length ||
				!session?.service_id
			)
				return [];
			if (myClub?.paid !== true) return [];

			const clubBenefitsList = sub.club?.benefits ?? [];
			const userBenefits =
				sub.userClubSubscriptionBenefits as (UserClubSubscriptionBenefitForEligibility & {
					club_benefit_id: string;
				})[];

			return userBenefits
				.map((ub) => {
					const cb =
						ub.club_benefit ??
						clubBenefitsList.find(
							(b: { id: string }) => b.id === ub.club_benefit_id,
						);
					if (!cb || !('service_id' in cb)) return null;
					if (cb.service_id !== session.service_id) return null;
					const serviceType = cb.service?.type;
					if (serviceType !== SERVICE_TYPE_PROFESSION) return null;
					const defaultAmount =
						'default_amount' in cb ? cb.default_amount : 0;
					if (
						!getBenefitHasAvailableUses(
							defaultAmount,
							ub.used_count,
						)
					)
						return null;
					const cbFull = cb as {
						service_id: string;
						default_amount?: number;
						service_substitute_title?: string | null;
						service_discount_percentage?: number;
						display_order?: number;
						service?: {
							id: string;
							type?: string;
							service_name?: string;
						};
					};
					return {
						...ub,
						club_benefit: {
							service_id: cbFull.service_id,
							default_amount: cbFull.default_amount ?? 0,
							service: cbFull.service,
							service_substitute_title:
								cbFull.service_substitute_title ?? null,
							service_discount_percentage:
								cbFull.service_discount_percentage ?? 100,
							display_order: cbFull.display_order ?? 0,
						},
					} as UserClubSubscriptionBenefitForEligibility;
				})
				.filter(
					(
						benefit,
					): benefit is UserClubSubscriptionBenefitForEligibility =>
						benefit !== null,
				)
				.sort(
					(a, b) =>
						(b.club_benefit.display_order ?? 0) -
						(a.club_benefit.display_order ?? 0),
				);
		}, [myClub, session?.service_id]);

	const upsellTeaser = useMemo(() => {
		if (!clubsAvailable?.length || !session.service_id) return null;
		const candidates = clubsAvailable
			.map((club) => {
				const benefits = findProfessionBenefitsForService(
					club,
					session.service_id,
				);
				if (!benefits.length) return null;
				const priceNum = Number(club.price);
				return {
					club,
					benefits,
					priceNum: Number.isFinite(priceNum)
						? priceNum
						: Number.POSITIVE_INFINITY,
				};
			})
			.filter((x): x is NonNullable<typeof x> => x !== null);
		if (!candidates.length) return null;
		candidates.sort((a, b) => {
			if (a.priceNum !== b.priceNum) return a.priceNum - b.priceNum;
			return a.club.id.localeCompare(b.club.id);
		});
		const first = candidates[0];
		const pct = Math.min(
			100,
			Math.max(0, first.benefits[0].service_discount_percentage ?? 0),
		);
		return { club: first.club, benefits: first.benefits, pct };
	}, [clubsAvailable, session.service_id]);

	const upsellSyntheticBenefits =
		useMemo((): UserClubSubscriptionBenefitForEligibility[] => {
			if (!clubsAvailable?.length || !session.service_id) return [];
			const rows: UserClubSubscriptionBenefitForEligibility[] = [];
			for (const club of clubsAvailable) {
				const benefits = findProfessionBenefitsForService(
					club,
					session.service_id,
				);
				if (!benefits.length) continue;
				for (const benefit of benefits) {
					if (
						rows.some(
							(r) =>
								r.club_benefit.service_id ===
									benefit.service_id &&
								r.club_benefit.service_discount_percentage ===
									benefit.service_discount_percentage,
						)
					) {
						continue;
					}
					rows.push({
						id: `${club.id}:${benefit.id}`,
						used_count: 0,
						club_benefit_id: benefit.id,
						club_benefit: {
							service_id: benefit.service_id ?? '',
							default_amount: benefit.default_amount ?? 0,
							service: benefit.service ?? undefined,
							service_substitute_title:
								benefit.service_substitute_title ?? null,
							service_discount_percentage:
								benefit.service_discount_percentage ?? 100,
							display_order: benefit.display_order ?? 0,
						} as UserClubSubscriptionBenefitForEligibility['club_benefit'],
					});
				}
			}
			rows.sort(
				(a, b) =>
					(b.club_benefit.service_discount_percentage ?? 0) -
					(a.club_benefit.service_discount_percentage ?? 0),
			);
			return rows;
		}, [clubsAvailable, session.service_id]);

	const upsellDisplayPct =
		upsellSyntheticBenefits[0]?.club_benefit.service_discount_percentage ??
		upsellTeaser?.pct ??
		0;

	const showClubBenefitCoupons = eligibleBenefits.length > 0;

	const showClubSubscriptionUpsell = Boolean(
		isCurrentSessionPayable &&
			hasSchedulingHistoryForClubUpsell === true &&
			!hasActiveClubSubscription &&
			upsellTeaser,
	);

	const professionalForService =
		professional ?? session.professional ?? undefined;
	const sessionServiceFromProfessional =
		professionalForService?.professional_services?.find(
			(ps) => ps.service?.id === session.service_id,
		)?.service;
	const resolvedService = sessionServiceFromProfessional ?? service;
	const serviceName =
		sessionServiceFromProfessional?.service_name ??
		service?.service_name ??
		session.service_name;
	const sessionHeadingTitle = getSessionDisplayTitle(session);
	const professionalLabel =
		sessionServiceFromProfessional?.professional_label ??
		service?.professional_label;

	const isPsychologistProfessional = useMemo(
		() =>
			isPsychologistServiceText(
				serviceName,
				professionalLabel,
				session.service_name,
				resolvedService?.service_name,
				resolvedService?.professional_label,
			),
		[
			serviceName,
			professionalLabel,
			session.service_name,
			resolvedService?.service_name,
			resolvedService?.professional_label,
		],
	);

	const photoUrl =
		professional?.profile_photo_url ??
		session.professional?.profile_photo_url ??
		undefined;
	const [photoLoaded, setPhotoLoaded] = useState(false);
	const [photoError, setPhotoError] = useState(false);
	const showPhoto = photoUrl && photoLoaded && !photoError;

	useEffect(() => {
		setPhotoLoaded(false);
		setPhotoError(false);
	}, [photoUrl]);

	const [selectedBenefit, setSelectedBenefit] =
		useState<UserClubSubscriptionBenefitForEligibility | null>(null);
	const [isBenefitPickerOpen, setIsBenefitPickerOpen] = useState(false);
	const [isJoiningSessionCall, setIsJoiningSessionCall] = useState(false);

	const handlePickClubBenefit = (
		benefit: UserClubSubscriptionBenefitForEligibility | null,
	) => {
		setSelectedBenefit(benefit);
		setIsBenefitPickerOpen(false);
	};

	useEffect(() => {
		if (
			selectedBenefit &&
			!eligibleBenefits.some((b) => b.id === selectedBenefit.id)
		) {
			setSelectedBenefit(null);
		}
	}, [eligibleBenefits, selectedBenefit]);

	useEffect(() => {
		if (!eligibleBenefits.length || selectedBenefit) return;
		const intent = readAndClearUpsellBenefitIntent();
		if (!intent || intent.serviceId !== session.service_id) return;
		const match =
			eligibleBenefits.find(
				(b) =>
					b.club_benefit.service_discount_percentage ===
					intent.discountPct,
			) ??
			eligibleBenefits.reduce((best, b) =>
				Math.abs(
					b.club_benefit.service_discount_percentage -
						intent.discountPct,
				) <
				Math.abs(
					best.club_benefit.service_discount_percentage -
						intent.discountPct,
				)
					? b
					: best,
			);
		setSelectedBenefit(match);
	}, [eligibleBenefits, session.service_id]);

	const formatCurrency = (value: number) =>
		new Intl.NumberFormat('pt-BR', {
			style: 'currency',
			currency: 'BRL',
		}).format(value);

	const MethodButton = ({
		title,
		subtitle,
		middleContent,
		icon: Icon,
		onClick,
		disabled,
		selected,
		animatedIconBorder,
		dataTestId,
	}: {
		title?: string;
		subtitle?: string;
		/** Quando definido, substitui título + subtítulo (ex.: cupom só com desconto e usos). */
		middleContent?: ReactNode;
		icon: React.ComponentType<{ className?: string }>;
		onClick: () => void;
		disabled?: boolean;
		selected?: boolean;
		animatedIconBorder?: boolean;
		dataTestId?: string;
	}) => {
		return (
			<button
				type="button"
				data-testid={dataTestId}
				onClick={onClick}
				disabled={disabled}
				className={cn(
					'group box-border flex w-full items-center gap-3 rounded-2xl border-2 px-3 py-4 text-left',
					'transition-all duration-150',
					'disabled:opacity-50',
					selected
						? [
								'border-brand-orange bg-white shadow-none',
								'translate-x-1 translate-y-1',
								'disabled:hover:translate-x-1 disabled:hover:translate-y-1 disabled:hover:shadow-none',
						  ]
						: [
								'border-stone-300 bg-stone-50',
								'shadow-[4px_4px_0px_0px_rgba(168,162,158,0.55)]',
								'hover:translate-x-[2px] hover:translate-y-[2px] hover:border-brand-orange hover:shadow-none',
								'disabled:hover:translate-x-0 disabled:hover:translate-y-0',
								'disabled:hover:border-stone-300',
								'disabled:hover:shadow-[4px_4px_0px_0px_rgba(168,162,158,0.55)]',
						  ],
				)}
			>
				{animatedIconBorder && !selected ? (
					<div className="relative size-10 shrink-0 overflow-hidden rounded-xl">
						<div
							className="absolute -left-1/2 -top-1/2 h-[200%] w-[200%] animate-gift-border-spin"
							style={{
								background:
									'conic-gradient(from 0deg, oklch(84.1% 0.238 128.85) 0%, oklch(84.1% 0.238 128.85) 40%, #4ade80 50%, #4ade80 90%, oklch(84.1% 0.238 128.85) 100%)',
							}}
						/>
						<div className="absolute inset-[2px] z-10 flex items-center justify-center overflow-hidden rounded-[9px] bg-white transition-colors duration-150 group-hover:bg-green-50">
							<Icon className="size-5" />
						</div>
					</div>
				) : (
					<div
						className={cn(
							'shrink-0 rounded-xl border-2 p-2 transition-colors duration-150',
							selected
								? 'border-brand-orange/40 bg-brand-orange/10'
								: 'border-stone-200 bg-white group-hover:border-brand-orange/40 group-hover:bg-brand-orange/5',
						)}
					>
						<Icon
							className={cn(
								'size-5',
								selected
									? 'text-brand-orange'
									: 'text-stone-400 group-hover:text-brand-orange',
							)}
						/>
					</div>
				)}
				<div className="min-w-0 flex-1">
					{middleContent != null ? (
						<div className="space-y-0.5">{middleContent}</div>
					) : (
						<>
							<h4 className="truncate text-sm font-bold text-stone-800 md:text-base">
								{title}
							</h4>
							{subtitle && (
								<p className="truncate text-xs font-medium text-stone-600">
									{subtitle}
								</p>
							)}
						</>
					)}
				</div>
				<div
					className={cn(
						'shrink-0 rounded-lg border-2 p-1.5 transition-all duration-150',
						selected
							? 'border-brand-orange bg-brand-orange'
							: [
									'border-stone-300 bg-white',
									'shadow-[3px_3px_0px_0px_rgba(168,162,158,0.45)]',
									'group-hover:border-brand-orange group-hover:bg-brand-orange group-hover:shadow-none',
							  ],
					)}
				>
					{selected ? (
						<Check
							className="size-4 text-white"
							strokeWidth={2.5}
							aria-hidden
						/>
					) : (
						<ChevronRight
							className="size-4 text-stone-400 transition-colors duration-150 group-hover:text-white"
							aria-hidden
						/>
					)}
				</div>
			</button>
		);
	};

	const handlePayment = async (
		gateway: Sessions.PaymentGateway,
		user_club_subscription_benefit_id?: string,
	) => {
		if (isCheckoutBusy) return;
		openModal({
			title: 'Processando',
			content: (
				<div className="flex flex-col items-center justify-center gap-4 py-8">
					<Loader2 className="size-12 animate-spin text-stone-400" />
					<p className="text-stone-600">Aguarde um momento...</p>
				</div>
			),
		});
		setConfirmText('');
		setCancelText('');

		try {
			let activeSession = session;
			if (sessionCreatePayload && !session.id) {
				activeSession = await createSession(sessionCreatePayload);
				onSessionCreated?.(activeSession);
			}

			const response = await paySession({
				id: activeSession.id,
				gateway,
				user_club_subscription_benefit_id,
			});
			if (response.url) {
				onPaymentClick?.();
				closeAllModals();
				window.location.href = response.url;
			} else if (response.payment?.status === 'DONE') {
				onPaymentClick?.();
				if (onPaymentSettled) {
					closeAllModals();
					onPaymentSettled();
				} else {
					closeAllModals();
					handleSuccess();
				}
			} else {
				closeAllModals();
				handleError();
			}
		} catch (error) {
			closeAllModals();
			handleError();
		}
	};

	const openUpsellClubSubscriptionModal = (
		clubs: Clubs.Club[],
		serviceId: string,
	) => {
		const returnUrl = buildCheckoutRedirectUrl(
			schedulingCheckoutReturn
				? `/consulta?d=${encodeUrl({
						s: 2,
						p: schedulingCheckoutReturn.professionalId,
						session: schedulingCheckoutReturn.sessionId,
				  })}`
				: `/consulta?d=${encodeUrl({ s: 3, session: session.id })}`,
		);
		setCancelText('');
		setConfirmText('');
		setOnConfirm(() => {});
		const sorted = sortClubsForUpsell(clubs, serviceId);
		openModal({
			title: 'Assinar um clube',
			size: 'wide',
			content: (
				<UpsellClubSubscriptionContent
					sorted={sorted}
					serviceId={serviceId}
					returnUrl={returnUrl}
				/>
			),
		});
	};

	const openUpsellBenefitPickerModal = () => {
		const clubsSnapshot = clubsAvailable ?? [];
		const heading = professionalLabel ?? sessionHeadingTitle;
		openModal({
			title: 'Benefícios disponíveis',
			content: (
				<div className="w-full space-y-3 px-0 py-1 md:space-y-4 md:py-0">
					<p className="text-sm leading-relaxed text-stone-500 md:text-[15px] md:leading-relaxed">
						Selecione o benefício que deseja ativar.
					</p>
					<div className="flex flex-col gap-2.5 md:gap-3.5">
						{upsellSyntheticBenefits.map((benefit, index) => (
							<BenefitPickerOptionRow
								key={benefit.id}
								benefit={benefit}
								headingServiceName={heading}
								hideUsageRemaining
								recommended={index === 0}
								onClick={() => {
									saveUpsellBenefitIntent(
										session.service_id,
										benefit.club_benefit
											.service_discount_percentage,
									);
									closeModal();
									openUpsellClubSubscriptionModal(
										clubsSnapshot,
										session.service_id,
									);
								}}
							/>
						))}
						<MethodButton
							title="Agora não"
							subtitle="Continuar sem assinar"
							icon={X}
							onClick={() => closeModal()}
						/>
					</div>
					<button
						type="button"
						onClick={() =>
							openModal({
								title: 'Termos de Uso do Benefício',
								content: <BenefitTermsOfUsePage />,
							})
						}
						className="text-xs font-semibold text-stone-700 underline-offset-2 hover:underline"
					>
						Termos de Uso do Benefício
					</button>
				</div>
			),
		});
	};

	const handleSuccess = () => {
		openModal({
			title: 'Pagamento Confirmado',
			content: (
				<div className="flex flex-col items-center gap-3 py-6">
					<CheckCircle2 className="size-16 text-green-500" />
					<h3 className="text-xl font-semibold">Tudo pronto!</h3>
				</div>
			),
		});
		setConfirmText('Voltar ao Início');
		setOnConfirm(() => appNavigate('/home'));
	};

	const handleError = () => {
		openModal({
			title: 'Falha no Pagamento',
			content: (
				<div className="flex flex-col items-center gap-3 py-6">
					<AlertCircle className="size-16 text-red-500" />
					<p className="text-stone-600">
						Erro ao processar pagamento.
					</p>
				</div>
			),
		});
		setConfirmText('Tentar novamente');
		setOnConfirm(closeModal);
	};

	const paymentsList =
		(session.payments as unknown as Sessions.PaymentDetails[]) || [];
	const existingPayments = paymentsList.filter(
		(p) => p.checkout_url && p.status === 'WAITING',
	);
	const hasPendingCheckout = existingPayments.length > 0;
	const isSessionPayable = session.status === 'CREATED';
	const hasPaymentBeenResolved = !hasPendingCheckout && !isSessionPayable;

	const isResolvingClubBenefits =
		isCurrentSessionPayable && myClub === undefined && isFetchingMyClub;

	const isResolvingClubHistory =
		isCurrentSessionPayable &&
		hasSchedulingHistoryForClubUpsell === undefined &&
		mySessionsPage === undefined &&
		isFetchingMySessions;

	const isResolvingClubUpsell =
		isCurrentSessionPayable &&
		!hasActiveClubSubscription &&
		hasSchedulingHistoryForClubUpsell === true &&
		clubsAvailable === undefined &&
		isFetchingClubsAvailable;

	const showClubBenefitsPlaceholder =
		isSessionPayable &&
		!hasPendingCheckout &&
		(isResolvingClubBenefits ||
			isResolvingClubHistory ||
			isResolvingClubUpsell) &&
		!showClubBenefitCoupons &&
		!showClubSubscriptionUpsell;

	const isEligibleForFirstSessionPromo =
		!isLoggedIn || hasSchedulingHistoryForClubUpsell === false;

	const showFirstSessionPromo = Boolean(
		isCurrentSessionPayable &&
			!hasPendingCheckout &&
			!showClubBenefitCoupons &&
			!showClubSubscriptionUpsell &&
			isEligibleForFirstSessionPromo &&
			isPsychologistProfessional &&
			(!canLoadUserCheckoutData || !isResolvingClubHistory),
	);

	/** 2ª sessão ou clube: lembrete de cupom na Stripe (sem o card grande da 1ª sessão). */
	const showStripeCouponPasteHint = Boolean(
		isCurrentSessionPayable &&
			!hasPendingCheckout &&
			!showFirstSessionPromo &&
			!isLoading &&
			(showClubBenefitCoupons ||
				hasSchedulingHistoryForClubUpsell === true),
	);

	const isUserAccessUnlocked = useUserSessionAccessWindow(session);
	const canShowJoinSessionButton =
		session.is_online &&
		(session.status === 'CONFIRMED' || session.status === 'STARTED');
	const waitingPaymentWithBenefit = existingPayments.find(
		(p) => p.payment_benefit_application,
	);
	const pendingBenefitDiscountRaw =
		waitingPaymentWithBenefit?.payment_benefit_application
			?.amount_discounted;
	const pendingBenefitDiscountAmount =
		pendingBenefitDiscountRaw != null && pendingBenefitDiscountRaw !== ''
			? Number(pendingBenefitDiscountRaw)
			: 0;

	const serviceValue = Number(
		(resolvedService as Services.Service | undefined)?.value || 0,
	);
	const discountPct = selectedBenefit
		? Math.min(
				100,
				Math.max(
					0,
					selectedBenefit.club_benefit.service_discount_percentage,
				),
		  )
		: 0;
	const discountAmountFromSelection = (serviceValue * discountPct) / 100;
	const appliedBenefitDiscountAmount =
		pendingBenefitDiscountAmount > 0
			? pendingBenefitDiscountAmount
			: discountAmountFromSelection;
	const totalValue = Math.max(0, serviceValue - appliedBenefitDiscountAmount);

	const professionalShareNum = Number(
		(resolvedService as Services.Service | undefined)
			?.professional_share_value || 0,
	);
	const hasPriceSplit =
		serviceValue > 0 &&
		professionalShareNum > 0 &&
		professionalShareNum + 1e-9 < serviceValue;
	const platformOperationCost = hasPriceSplit
		? Math.round((serviceValue - professionalShareNum) * 100) / 100
		: 0;
	const attendanceLineAmount = hasPriceSplit
		? professionalShareNum
		: serviceValue;

	const handlePayButtonClick = () => {
		const pendingCheckoutUrl = existingPayments.find((p) => p.checkout_url)
			?.checkout_url;
		if (hasPendingCheckout && pendingCheckoutUrl) {
			onPaymentClick?.();
			closeAllModals();
			window.location.href = pendingCheckoutUrl;
			return;
		}

		if (
			isDeferredSessionCreation &&
			!getUserData() &&
			sessionCreatePayload
		) {
			saveSchedulingPayDraft(sessionCreatePayload);
			markSchedulingPayHandoffToAuth();
			onPaymentClick?.();
			closeAllModals();
			appNavigate(`/agendamento?d=${encodeUrl({ s: 3 })}`, {
				replace: true,
			});
			return;
		}

		void handlePayment('STRIPE', selectedBenefit?.id);
	};

	const dateObj = new Date(session.selected_date);
	const time = dateObj.toLocaleTimeString('pt-BR', {
		hour: '2-digit',
		minute: '2-digit',
	});
	const fullDateLabel = dateObj.toLocaleDateString('pt-BR', {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
	const modalityIsOnline = Boolean(session.is_online);
	const paymentResolvedTitle =
		session.status === 'STARTED'
			? 'Sessão iniciada'
			: session.status === 'FINISHED'
			  ? 'Sessão finalizada'
			  : session.status === 'CANCELLED'
			    ? 'Sessão cancelada'
			    : 'Pagamento confirmado';
	const paymentResolvedDescription =
		session.status === 'STARTED'
			? isUserAccessUnlocked
				? 'Seu pagamento foi confirmado e a sessão já foi iniciada. Você já pode entrar na chamada.'
				: 'Seu pagamento foi confirmado. O acesso à reunião será liberado 5 minutos antes do horário agendado.'
			: session.status === 'FINISHED'
			  ? 'Seu pagamento foi confirmado e a sessão já foi concluída.'
			  : session.status === 'CANCELLED'
			    ? 'O pagamento foi processado, mas a sessão está cancelada no momento.'
			    : modalityIsOnline
			      ? 'Seu pagamento foi confirmado. O acesso à reunião será liberado automaticamente 5 minutos antes do horário agendado.'
			      : 'Seu pagamento foi confirmado. A sessão aparecerá na sua página inicial com os dados do atendimento.';
	const sessionTypeChip =
		session.type === 'RETURN'
			? 'Retorno'
			: session.type === 'GROUP'
			  ? `Grupo · até ${session.max_participants ?? '—'} pessoas`
			  : null;

	const professionalForName = {
		name_prefix:
			professional?.name_prefix ??
			session.professional?.name_prefix ??
			null,
		first_name:
			professional?.first_name ?? session.professional?.first_name ?? '',
		last_name:
			professional?.last_name ?? session.professional?.last_name ?? '',
	};
	const professionalDisplay = formatProfessionalFullName(professionalForName);
	const professionalSubtitle = professionalDisplay || professionalLabel || '';

	return (
		<div className="relative w-full">
			<PaymentEmbeddedScrollHint
				enabled={
					embeddedInModal &&
					!isLoading &&
					isSessionPayable &&
					!isBenefitPickerOpen
				}
			/>
			<div
				className={cn(
					'box-border flex w-full min-w-0 animate-fade-in flex-col gap-6 bg-transparent px-1',
					isSessionPayable &&
						!embeddedInModal &&
						!isBenefitPickerOpen &&
						'pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-0',
					className,
				)}
			>
				{isBenefitPickerOpen && showClubBenefitCoupons ? (
					<ClubBenefitPickerPanel
						benefits={eligibleBenefits}
						headingServiceName={
							professionalLabel ?? sessionHeadingTitle
						}
						selectedBenefitId={selectedBenefit?.id ?? null}
						onPick={handlePickClubBenefit}
						onClose={() => setIsBenefitPickerOpen(false)}
					/>
				) : (
					<>
						{isLoading ? (
							<PaymentModalContainerSkeleton />
						) : (
							<div className="flex flex-col rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
								<div className="mb-4 flex flex-wrap items-start justify-between gap-4">
									<div className="space-y-1">
										<p className="text-[10px] font-black uppercase tracking-widest text-brand-orange">
											Agendamento
										</p>
										<h2 className="text-xl font-black leading-snug text-stone-900">
											Sua consulta
										</h2>
									</div>
									<div
										className={cn(
											'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-widest',
											modalityIsOnline
												? 'border-brand-orange/30 bg-brand-orange/10 text-brand-orange'
												: 'border-stone-200 bg-stone-50 text-stone-700',
										)}
									>
										{modalityIsOnline ? (
											<Video className="size-3.5 shrink-0 text-brand-orange" />
										) : (
											<MapPin className="size-3.5 shrink-0 text-stone-500" />
										)}
										<span>
											{modalityIsOnline
												? 'Online'
												: 'Presencial'}
											{sessionTypeChip
												? ` · ${sessionTypeChip}`
												: ''}
										</span>
									</div>
								</div>

								<div className="flex flex-col gap-5">
									<div className="flex items-start gap-4">
										<div className="relative shrink-0">
											{photoUrl && !photoError ? (
												<div className="relative size-16 sm:size-20">
													{!showPhoto && (
														<div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl border border-stone-100 bg-white">
															<Loader2 className="size-6 animate-spin text-stone-400 sm:size-8" />
														</div>
													)}
													<img
														src={photoUrl}
														className={cn(
															'size-16 rounded-2xl border border-stone-100 object-cover shadow-sm transition-opacity sm:size-20',
															showPhoto
																? 'opacity-100'
																: 'opacity-0',
														)}
														alt="Profissional"
														onLoad={() =>
															setPhotoLoaded(true)
														}
														onError={() =>
															setPhotoError(true)
														}
													/>
												</div>
											) : (
												<div className="flex size-16 items-center justify-center rounded-2xl border border-stone-100 bg-stone-50 sm:size-20">
													<User className="size-7 text-stone-300 sm:size-9" />
												</div>
											)}
										</div>
										<div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
											<h3 className="text-lg font-bold leading-snug text-stone-900 sm:text-xl">
												{sessionHeadingTitle}
											</h3>
											{professionalSubtitle && (
												<p className="text-sm font-semibold text-stone-600">
													{professionalSubtitle}
												</p>
											)}
										</div>
									</div>

									<div className="flex flex-col gap-4 pt-5">
										<div className="flex min-w-0 items-start gap-3">
											<div className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-brand-orange/25 bg-brand-orange/10 text-brand-orange shadow-sm">
												<Calendar
													className="size-4"
													aria-hidden
												/>
											</div>
											<div className="min-w-0 pt-0.5">
												<p className="text-[10px] font-black uppercase tracking-widest text-brand-orange">
													Data
												</p>
												<p className="text-sm font-semibold capitalize leading-snug text-stone-900">
													{fullDateLabel}
												</p>
											</div>
										</div>
										<div className="flex items-center gap-3">
											<div className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-brand-orange/25 bg-brand-orange/10 text-brand-orange shadow-sm">
												<Clock
													className="size-4"
													aria-hidden
												/>
											</div>
											<div className="pt-0.5">
												<p className="text-[10px] font-black uppercase tracking-widest text-brand-orange">
													Horário
												</p>
												<p className="text-sm font-semibold leading-snug text-stone-900">
													{time}
												</p>
											</div>
										</div>

										<div className="flex gap-3 rounded-xl border border-stone-100 bg-stone-50/40 p-4 text-sm leading-relaxed text-stone-600">
											<div className="mt-0.5 shrink-0 text-stone-500">
												{modalityIsOnline ? (
													<Video
														className="size-4"
														aria-hidden
													/>
												) : (
													<MapPin
														className="size-4"
														aria-hidden
													/>
												)}
											</div>
											<p>
												{modalityIsOnline
													? 'Você receberá o link da videochamada após a confirmação do pagamento.'
													: 'Compareça ao local combinado com o profissional na data e horário acima.'}
											</p>
										</div>
									</div>
								</div>
							</div>
						)}

						{isSessionPayable && !hasPendingCheckout && (
							<>
								{showClubBenefitsPlaceholder && (
									<div
										className="h-[5.5rem] animate-pulse rounded-2xl bg-stone-100"
										aria-hidden
									/>
								)}
								{showFirstSessionPromo && (
									<div
										className="flex flex-col gap-3 rounded-2xl border border-brand-orange/20 bg-white p-4 shadow-sm"
										data-testid={
											E2E_SCHEDULING.FIRST_SESSION_PROMO
										}
									>
										<div className="flex items-start gap-3 bg-white p-4 pt-1">
											<div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-brand-orange/25 bg-brand-orange/10 text-brand-orange">
												<Tag
													className="size-4"
													aria-hidden
												/>
											</div>
											<div className="min-w-0 space-y-1">
												<p className="text-[9px] font-black uppercase tracking-[0.14em] text-brand-orange">
													Primeira consulta
												</p>
												<h3 className="text-lg font-bold leading-snug text-stone-900">
													Sua primeira sessão por{' '}
													{formatCurrency(
														SCHEDULING_FIRST_SESSION_PROMO_PRICE_BRL,
													)}
												</h3>
											</div>
										</div>

										<div className="bg-white px-4 py-1">
											<p className="text-xs leading-relaxed text-stone-600">
												Copie o código e cole na próxima
												tela para aplicar o valor
												promocional.
											</p>
										</div>

										<div className="space-y-2 bg-white px-4 pb-1 pt-0">
											<p className="text-[8px] font-black uppercase tracking-widest text-stone-500">
												Cupom (use na tela de pagamento)
											</p>
											<CopyWrapper
												valueToCopy={
													SCHEDULING_FIRST_SESSION_PROMO_CODE
												}
												wrapperClassName="w-full"
												buttonClassName="right-3"
											>
												<div
													className="rounded-xl border border-stone-200 bg-stone-50 px-4 py-2.5 pr-12 font-mono text-sm font-bold tracking-wide text-stone-900"
													data-testid={
														E2E_SCHEDULING.FIRST_SESSION_PROMO_CODE
													}
												>
													{
														SCHEDULING_FIRST_SESSION_PROMO_CODE
													}
												</div>
											</CopyWrapper>
										</div>
									</div>
								)}
								{showClubBenefitCoupons && (
									<div
										className="flex flex-col rounded-2xl border border-stone-100 bg-white p-5 shadow-sm"
										data-testid={
											E2E_SCHEDULING.CLUB_BENEFIT_SECTION
										}
									>
										<div className="mb-4">
											<h3 className="text-lg font-bold leading-snug text-stone-900">
												Benefício do clube
											</h3>
										</div>
										<MethodButton
											dataTestId={
												E2E_SCHEDULING.CLUB_BENEFIT_PICKER_OPEN
											}
											{...(selectedBenefit
												? {
														middleContent: (
															<>
																<p className="truncate text-sm font-extrabold text-stone-900">
																	<BenefitDiscountHighlightLabel
																		benefit={
																			selectedBenefit
																		}
																	/>
																</p>
																<p className="truncate text-xs font-medium text-stone-600">
																	{getBenefitRemainingBalance(
																		selectedBenefit
																			.club_benefit
																			.default_amount,
																		selectedBenefit.used_count,
																	)}{' '}
																	uso(s)
																	restante(s)
																</p>
															</>
														),
												  }
												: {
														title: 'Benefício',
														subtitle:
															'Escolha um benefício de desconto',
												  })}
											icon={Gift}
											onClick={() =>
												setIsBenefitPickerOpen(true)
											}
											disabled={isPaying}
											selected={!!selectedBenefit}
										/>
									</div>
								)}
								{showClubSubscriptionUpsell && (
									<div className="flex flex-col rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
										<div className="mb-4 space-y-0.5">
											<p className="text-[9px] font-medium uppercase tracking-[0.14em] text-brand-orange">
												Benefício do clube
											</p>
											<h3 className="text-lg font-bold leading-snug text-stone-900">
												Assinatura
											</h3>
										</div>
										<MethodButton
											dataTestId={
												E2E_SCHEDULING.CLUB_SUBSCRIPTION_UPSELL
											}
											middleContent={
												<>
													<p className="relative overflow-hidden truncate text-sm font-extrabold text-stone-900">
														Garanta até{' '}
														<span className="text-brand-green">
															{upsellDisplayPct}%
														</span>{' '}
														de desconto
														<span
															className="pointer-events-none absolute inset-y-0 left-0 w-[80%] animate-shimmer"
															style={{
																background:
																	'linear-gradient(to right, transparent 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0) 70%, transparent 100%)',
															}}
															aria-hidden
														/>
													</p>
													<p className="truncate text-xs font-normal text-stone-500">
														Economize agora mesmo
														até{' '}
														<span className="font-semibold text-stone-700">
															{formatCurrency(
																totalValue *
																	(upsellDisplayPct /
																		100),
															)}
														</span>{' '}
														no atendimento
													</p>
												</>
											}
											icon={AnimatedGiftIcon}
											animatedIconBorder={true}
											onClick={() =>
												openUpsellBenefitPickerModal()
											}
											disabled={isPaying || isJoiningClub}
											selected={false}
										/>
									</div>
								)}
							</>
						)}

						{showStripeCouponPasteHint && (
							<div
								className="rounded-2xl border border-brand-orange/15 bg-brand-orange/[0.06] px-4 py-3.5 shadow-sm"
								data-testid={
									E2E_SCHEDULING.PAYMENT_STRIPE_COUPON_HINT
								}
							>
								<p className="text-xs leading-relaxed text-stone-600 sm:text-[13px]">
									Na próxima tela você poderá colar um cupom
									promocional caso possua um.
								</p>
							</div>
						)}

						{!isLoading && (
							<div className="flex flex-col rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
								<div className="mb-4">
									<h2 className="text-xl font-black leading-snug text-stone-900">
										Resumo de valores
									</h2>
								</div>
								<div className="space-y-3 text-sm">
									<div className="flex justify-between gap-4">
										<span className="text-stone-600">
											Atendimento
										</span>
										<span className="font-semibold tabular-nums text-stone-900">
											{formatCurrency(
												attendanceLineAmount,
											)}
										</span>
									</div>
									<div className="flex justify-between gap-4">
										<span className="text-stone-600">
											Custo de operação
										</span>
										{platformOperationCost > 0 ? (
											<span className="font-semibold tabular-nums text-brand-green">
												{formatCurrency(
													platformOperationCost,
												)}
											</span>
										) : (
											<div className="flex items-center gap-2">
												<span className="text-[10px] font-medium tabular-nums text-stone-400 line-through">
													{formatCurrency(
														DISPLAY_OPERATION_FEE_REFERENCE_BRL,
													)}
												</span>
												<span className="font-bold text-brand-green">
													Grátis
												</span>
											</div>
										)}
									</div>
									<div className="flex justify-between gap-4">
										<span className="text-stone-600">
											Desconto
										</span>
										<span
											className={cn(
												'font-semibold tabular-nums',
												appliedBenefitDiscountAmount > 0
													? 'text-brand-green'
													: 'text-stone-500',
											)}
										>
											{appliedBenefitDiscountAmount > 0
												? '−'
												: ''}
											{formatCurrency(
												appliedBenefitDiscountAmount,
											)}
										</span>
									</div>
									<div className="mt-3 border-t border-stone-200 pt-4">
										<div className="flex justify-between gap-4">
											<span className="font-bold text-stone-900">
												Total a pagar
											</span>
											<span className="text-lg font-black tabular-nums text-stone-900">
												{formatCurrency(totalValue)}
											</span>
										</div>
									</div>
								</div>
							</div>
						)}

						{hasPaymentBeenResolved && (
							<div className="flex flex-col rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5 shadow-sm">
								<div className="flex items-start gap-3">
									<div className="mt-0.5 flex size-11 shrink-0 items-center justify-center rounded-2xl border border-emerald-200 bg-white text-emerald-600 shadow-sm">
										<CheckCircle2
											className="size-5"
											aria-hidden
										/>
									</div>
									<div className="min-w-0 flex-1 space-y-1">
										<h3 className="text-lg font-bold leading-snug text-stone-900">
											{paymentResolvedTitle}
										</h3>
										<p className="text-sm leading-relaxed text-stone-600">
											{paymentResolvedDescription}
										</p>
									</div>
								</div>

								<div className="mt-5 flex flex-col gap-3 sm:flex-row">
									{canShowJoinSessionButton && (
										<button
											type="button"
											onClick={() =>
												void openUserSessionCall({
													sessionId: session.id,
													navigate: appNavigate,
													sessionStatus:
														session.status,
													onStart: () =>
														setIsJoiningSessionCall(
															true,
														),
													onFinally: () =>
														setIsJoiningSessionCall(
															false,
														),
												})
											}
											disabled={
												isJoiningSessionCall ||
												!isUserAccessUnlocked
											}
											className={cn(
												'inline-flex items-center justify-center rounded-xl border-2 border-zinc-900 bg-zinc-900 px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-white transition-all duration-150',
												isJoiningSessionCall ||
													!isUserAccessUnlocked
													? 'cursor-not-allowed opacity-60'
													: 'hover:bg-zinc-800',
											)}
										>
											{isJoiningSessionCall
												? 'Entrando...'
												: isUserAccessUnlocked
												  ? 'Entrar na chamada'
												  : 'Disponivel 5 min antes'}
										</button>
									)}
									<button
										type="button"
										onClick={() => appNavigate('/home')}
										className="inline-flex items-center justify-center rounded-xl border border-stone-300 bg-white px-5 py-3 text-sm font-bold text-stone-700 transition-all duration-150 hover:bg-stone-50"
									>
										Ir para minhas consultas
									</button>
								</div>
							</div>
						)}

						{isSessionPayable && (
							<div
								className={cn(
									embeddedInModal
										? 'mt-4 border-t border-stone-200 pt-4'
										: [
												'fixed bottom-0 left-0 right-0 z-50 border-t border-stone-200 bg-white/95 pt-3 shadow-[0_-8px_32px_-12px_rgba(0,0,0,0.15)] backdrop-blur-md',
												'pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]',
												'md:static md:z-auto md:mb-4 md:border-t-0 md:bg-transparent md:p-0 md:pb-0 md:shadow-none md:backdrop-blur-none',
										  ],
								)}
							>
								<div className="mx-auto w-full">
									<div
										className={cn(
											'flex w-full flex-col gap-3',
											!embeddedInModal &&
												'sm:flex-row sm:items-center sm:justify-between sm:rounded-2xl sm:border sm:border-stone-100 sm:bg-white sm:p-5 sm:shadow-sm',
										)}
									>
										<div
											className={cn(
												'flex w-full min-w-0 items-baseline justify-between gap-3',
												!embeddedInModal &&
													'sm:block sm:flex-1 sm:space-y-0.5',
											)}
										>
											<p
												className={cn(
													'shrink-0 text-xl font-black tabular-nums tracking-tight text-brand-orange',
													!embeddedInModal &&
														'sm:text-2xl md:text-3xl',
												)}
											>
												Valor final
											</p>
											<p
												className={cn(
													'shrink-0 text-right text-xl font-black tabular-nums tracking-tight text-stone-900',
													!embeddedInModal &&
														'sm:truncate sm:text-left sm:text-2xl md:text-3xl',
												)}
												data-testid={
													E2E_SCHEDULING.PAYMENT_FINAL_VALUE
												}
											>
												{formatCurrency(totalValue)}
											</p>
										</div>
										<button
											type="button"
											data-testid={
												E2E_SCHEDULING.PAYMENT_PAY_BUTTON
											}
											disabled={isCheckoutBusy}
											onClick={handlePayButtonClick}
											className={cn(
												'inline-flex h-11 w-full shrink-0 items-center justify-center border-2 border-brand-green bg-brand-green px-4',
												embeddedInModal
													? 'rounded-lg'
													: 'rounded-lg sm:rounded-xl',
												'text-sm font-black normal-case tracking-wide text-white shadow-sm transition-all duration-150',
												'hover:bg-brand-green/92 hover:border-brand-green active:scale-[0.98]',
												'disabled:scale-100 disabled:cursor-not-allowed disabled:border-stone-200 disabled:bg-stone-200 disabled:text-stone-500 disabled:shadow-none disabled:hover:bg-stone-200',
												!embeddedInModal &&
													'sm:w-max sm:max-w-none sm:px-8 sm:uppercase sm:tracking-[0.14em]',
											)}
										>
											{isCheckoutBusy ? (
												'Processando…'
											) : hasPendingCheckout ? (
												'Voltar ao pagamento'
											) : embeddedInModal ? (
												'Ir para o pagamento'
											) : (
												<>
													<span className="sm:hidden">
														Ir para o pagamento
													</span>
													<span className="hidden sm:inline">
														Pagar
													</span>
												</>
											)}
										</button>
									</div>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export { PaymentContainer };
